import { useState, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from '../../../components/ui/dialog';
import { Text } from '@folio/components/ui/text';
import { Stack } from '@folio/components/ui/flex';

type OtpPhoneNumberModalProps = {
  defaultPhoneNumber: string;
  onSend: (phoneNumber: string) => void | Promise<void>;
  customerPageUrl?: string;
  isLoading?: boolean;
};

export type OtpPhoneNumberModalRef = {
  open: () => void;
  close: () => void;
};

export const OtpPhoneNumberModal = forwardRef<OtpPhoneNumberModalRef, OtpPhoneNumberModalProps>(
  ({ defaultPhoneNumber, onSend, customerPageUrl, isLoading }, ref) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();

    useImperativeHandle(ref, () => ({
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
    }));

    const handleSubmit = async () => {
      await onSend(defaultPhoneNumber);
    };

    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader className="text-center">
            <DialogDescription className="text-center">
              {t('finalizeNewBusiness.otpModal.description')}
            </DialogDescription>
          </DialogHeader>

          <Stack>
            <Stack gap={2}>
              <div className="text-lg font-semibold text-center py-2">
                {defaultPhoneNumber || t('finalizeNewBusiness.otpModal.noPhoneNumber')}
              </div>

              {customerPageUrl && (
                <Text size="sm" tone="muted" className="text-center">
                  {t('finalizeNewBusiness.otpModal.wrongNumber')}{' '}
                  <button
                    type="button"
                    className="text-primary underline hover:text-primary/80 font-medium cursor-pointer"
                    onClick={() => {
                      setIsOpen(false);
                      navigate(customerPageUrl);
                    }}
                  >
                    {t('finalizeNewBusiness.otpModal.clickHere')}
                  </button>{' '}
                  {t('finalizeNewBusiness.otpModal.editOnCustomerPage')}
                </Text>
              )}
            </Stack>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
                disabled={isLoading}
              >
                {t('finalizeNewBusiness.otpModal.cancel')}
              </Button>
              <Button onClick={handleSubmit} disabled={isLoading || !defaultPhoneNumber}>
                {isLoading
                  ? t('finalizeNewBusiness.otpModal.sending')
                  : t('finalizeNewBusiness.otpModal.sendCode')}
              </Button>
            </DialogFooter>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  },
);

OtpPhoneNumberModal.displayName = 'OtpPhoneNumberModal';
